import React, { useCallback } from "react";
import i18n from "lib/i18n";
import PropTypes from "prop-types";
import { isEqual } from "underscore";
import { Typography, withStyles } from "@theconversation/ui";
import Dropdown from "components/MemoisedDropdown";
import TextField from "components/MemoisedTextField";
import FormFieldset from "components/FormFieldset";

import Analytics from "lib/Analytics";


const track = field => (event) => {
  if (event.target.value.length) {
    Analytics.trackFormEvent("Donation Form", "Completed", field);
  } else {
    Analytics.trackFormEvent("Donation Form", "Skipped", field);
  }
};

const styles = {
  contactDetails: {
    marginTop: 16,
  },
  menu: {
    width: 200,
  },
};

const TITLES = {
  en: ["Mr", "Mrs", "Ms", "Mx", "Dr", "Prof"],
  fr: ["M", "Mme", "Mlle", "Dr", "Pr"],
};
const LANGUAGES = Object.keys(TITLES);

const ContactDetails = ({
  active,
  classes,
  email,
  errors,
  lastName,
  name,
  onChange,
  title,
}) => {
  const trackName = useCallback(track("Name"));
  const trackLastName = useCallback(track("LastName"));
  const trackEmail = useCallback(track("Email"));

  const setEmail = useCallback(
    event => onChange({ email: event.target.value }),
    [onChange]
  );

  const setTitle = useCallback((event) => {
    track("Title")(event);
    onChange({ title: event.target.value });
  }, [onChange]);

  const setName = useCallback(
    event => onChange({ name: event.target.value }),
    [onChange]
  );

  const setLastName = useCallback(
    event => onChange({ lastName: event.target.value }),
    [onChange]
  );

  // Work through the fallback list of languages till we find one which we have
  // a title list for.
  const language = i18n.firstAvailableLanguage(LANGUAGES);
  const titleOptions = TITLES[language].map(titleStr =>
    <option key={titleStr} value={titleStr}>{titleStr}</option>
  );

  return (
    <div id="contact-details" className={classes.contactDetails}>
      <FormFieldset>
        <Typography variant="h6">{i18n.t("donation.heading.contact_details")}</Typography>
      </FormFieldset>

      <FormFieldset>
        <Dropdown
          className={classes.menu}
          disabled={!active}
          id="donation_title"
          label={i18n.t("donation.placeholder.your_title")}
          name="donation[title]"
          native
          onChange={setTitle}
          value={title}
        >
          <option value="" />
          {titleOptions}
        </Dropdown>
      </FormFieldset>

      <FormFieldset>
        <TextField
          autoComplete="given-name"
          disabled={!active}
          error={!!errors.name}
          fullWidth
          helperText={errors.name}
          id="donation_name"
          label={i18n.t("donation.placeholder.your_name")}
          name="donation[name]"
          onBlur={trackName}
          onChange={setName}
          type="text"
          value={name}
        />
      </FormFieldset>

      <FormFieldset>
        <TextField
          autoComplete
          disabled={!active}
          error={!!errors.lastName}
          fullWidth
          helperText={errors.lastName}
          id="donation_last_name"
          label={i18n.t("donation.placeholder.your_last_name")}
          name="donation[last_name]"
          onBlur={trackLastName}
          onChange={setLastName}
          type="text"
          value={lastName}
        />
      </FormFieldset>

      <FormFieldset>
        <TextField
          autoComplete
          disabled={!active}
          error={!!errors.email}
          fullWidth
          helperText={errors.email}
          id="donation_email"
          label={i18n.t("donation.placeholder.your_email_address")}
          name="donation[email]"
          onBlur={trackEmail}
          onChange={setEmail}
          type="email"
          value={email}
        />
      </FormFieldset>
    </div>
  );
};

ContactDetails.defaultProps = {
  active: true,
  classes: {
    menu: "",
  },
  email: "",
  errors: {},
  lastName: "",
  name: "",
  onChange: () => {},
  title: "",
};

ContactDetails.propTypes = {
  active: PropTypes.bool,
  classes: PropTypes.shape({
    menu: PropTypes.string,
  }),
  email: PropTypes.string,
  errors: PropTypes.shape({
    email: PropTypes.string,
    lastName: PropTypes.string,
    name: PropTypes.string,
    title: PropTypes.string,
  }),
  lastName: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  title: PropTypes.string,
};

export default withStyles(styles)(
  React.memo(
    ContactDetails,
    (prevProps, nextProps) => isEqual(prevProps, nextProps)
  )
);
